import type {
  GuildRankTitle,
  GuildSpecialTitle,
} from "@/core/guild-members/titles"

import { getCsrfTokenFromDocumentCookie } from "@/lib/auth-api"

export type AdminMemberProfile = {
  memberId: string
  displayName: string | null
  avatarUrl: string | null
  joinedAt: string | null
  rankTitle: GuildRankTitle | null
  specialTitles: GuildSpecialTitle[]
  activityPoints: Array<{ year: number; month: number; points: number }>
  lifecycle: {
    status: "pending" | "active" | "inactive"
    deactivatedAt: string | null
    deactivationNote: string | null
    discordPresence: "present" | "departed" | "unknown"
    discordPresenceChangedAt: string | null
  }
}

export class AdminMemberProfileApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message)
    this.name = "AdminMemberProfileApiError"
  }
}

const readProfile = async (
  response: Response,
  fallback: string,
): Promise<AdminMemberProfile> => {
  if (!response.ok) {
    let message = fallback
    let code: string | undefined
    try {
      const body: unknown = await response.json()
      if (
        body &&
        typeof body === "object" &&
        "error" in body &&
        body.error &&
        typeof body.error === "object"
      ) {
        if ("message" in body.error && typeof body.error.message === "string")
          message = body.error.message
        if ("code" in body.error && typeof body.error.code === "string")
          code = body.error.code
      }
    } catch {}
    throw new AdminMemberProfileApiError(message, response.status, code)
  }
  const payload = (await response.json()) as { profile: AdminMemberProfile }
  return payload.profile
}

export const adminMemberProfileApi = {
  async get(memberId: string) {
    return readProfile(
      await fetch(`/api/admin/members/${encodeURIComponent(memberId)}`, {
        credentials: "include",
      }),
      "Failed to load member profile",
    )
  },
  async deactivate(memberId: string, note: string) {
    const csrfToken = getCsrfTokenFromDocumentCookie()
    return readProfile(
      await fetch(
        `/api/admin/members/${encodeURIComponent(memberId)}/deactivate`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "content-type": "application/json",
            ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
          },
          body: JSON.stringify({ note }),
        },
      ),
      "Failed to deactivate membership",
    )
  },
  async setJoinDate(memberId: string, joinedDate: string | null) {
    const csrfToken = getCsrfTokenFromDocumentCookie()
    return readProfile(
      await fetch(
        `/api/admin/members/${encodeURIComponent(memberId)}/join-date`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "content-type": "application/json",
            ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
          },
          body: JSON.stringify({ joinedDate }),
        },
      ),
      "Failed to save guild join date",
    )
  },
}
